import { LitElement, html, css, TemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { RouteEditModel } from '../../../src/models/route-edit-model';
import { CoordinatesUtils } from '../../../src/utils/coordinates-utils';
import { debounce, deepCopy, getWaypointsFromRoute, mpsToKnots, ScrollTo, isStarRouteDeviationEnabled } from '../../../src/utils/helpers';
import { getLegTypeFromDist } from '../../../src/utils/map-utils';

import { VoyageDialogPopupManager } from '../../voyage-dialog-popup/src/voyage-dialog-popup-manager';
import { RoutingJobOptions, VoyageRoute, VoyageRoutePointFeature, VoyageRoutingModel } from '../../../src/models/voyage-routing-model'
import { voyageRadioButtonsStyle, voyageCursorStyles } from '../../voyage-style/src/voyage-style';
import '../../voy-toggle-button/src/voy-toggle-button';
import { VoyToggleButton } from '../../voy-toggle-button/src/voy-toggle-button';
import '../../voy-lat-long-input/src/voy-lat-long-input';
import { VoyLatLongInput } from '../../voy-lat-long-input/src/voy-lat-long-input';
import '../../voyage-spinner/src/voyage-spinner';
import { VesselCharacteristics, VesselPlanningModel, VoyagePlan } from '../../../src/models/vessel-planning-model';
import './voy-route-editor-validation-errors';
import { filterConditionNavigational, getMostFuelEfficientStarRoute, getWaypointDisplayNumbers, getWaypointDisplayStartIndex } from '../../../src/utils/route-utils';
import {
    formatZoneTime,
    getEntryExitPoints,
    getZoneCrossingItems,
    getZoneDescription,
    routeWarningsReviewed,
    wpCoordKey,
    ZoneCrossingItem,
    ZonePoint
} from '../../../src/utils/zone-crossing-utils';
import { CONST_POW_ENDPOINT_V2, FeatureFlagModel } from '../../../src/models/feature-flag-model';
import '@maersk-global/mds-components-core/mc-tooltip';

const MpsToKts = 60 * 60 / 1852;
// Zone labels carry the zone's own name and can be long, and a waypoint closing one zone while
// opening the next shows two of them. The row shows this many characters and keeps the whole label
// in a tooltip.
const ZoneLabelMaxChars = 40;
type ValidatoErrorType = "min-speed" | "max-speed" | "eta" | "etd";
export type ValidatonErrors = Map<ValidatoErrorType, Map<number, string>>

enum stoppageWaypointReason {
    WeatherRouting = "Weather routing reason",
    TechnicalIssue = "Technical issue",
    AwaitingETA = "Awaiting min. safe speed for ETA",
    BoardingCrews = "On or off boarding crew",
    BoardingGuards = "On or off boarding armed guards",
    Achorage = "Anchorage/drifting awaiting berth",
    Other = "My reason in not in the list"
  }
@customElement("voyage-route-editor")
export class VoyageRouteEditor extends LitElement {
    constructor() {
        super();

        this._add = this._add.bind(this);
        this._delete = this._delete.bind(this);
        this._updateLon = this._updateLon.bind(this);
        this._updateLat = this._updateLat.bind(this);
        this._inputClick = this._inputClick.bind(this);
        this._focusOut = this._focusOut.bind(this);
        this._saveAndClose = this._saveAndClose.bind(this);
        this._onRouteChanged = this._onRouteChanged.bind(this);
        this._onSelectionChanged = this._onSelectionChanged.bind(this);
        this._revert = this._revert.bind(this);
        this._debouncedUpdateEta = debounce(this._updateETA.bind(this), 250);
        this._debouncedUpdateEtd = debounce(this._updateETD.bind(this), 250);
        this._setFieldValidationError = this._setFieldValidationError.bind(this);
        this._deleteValidationError = this._deleteValidationError.bind(this);
        this._checkRouteValidity = this._checkRouteValidity.bind(this);
        this._checkWaypointValidity = this._checkWaypointValidity.bind(this);
        this._validateEtasChronological = this._validateEtasChronological.bind(this);
        this._validateStoppageTimings = this._validateStoppageTimings.bind(this);
        this._getVesselCharacteristics = this._getVesselCharacteristics.bind(this);
        this._checkRouteAlignment = this._checkRouteAlignment.bind(this);
    }

    @property({ attribute: false }) plan: VoyagePlan | null = null;
    @property({ attribute: false }) selectedRouteId: string | '' = '';
    @property({ attribute: false }) selectedStarRoute: string | '' = '';
    @property({ attribute: false }) planningScheduleId: string | '' = '';
    @property({ type: Function }) setStarRouteLayerVisibility!: (starRouteID: string, routeID: string) => void;
    @property({ attribute: false }) private _waypoints: VoyageRoutePointFeature[] = [];
    @property({ attribute: false }) private _saving: boolean = false;
    @property({ attribute: false }) private _vesselCharacteristics: VesselCharacteristics | null = null;
    // Zone point key ("lon,lat|zoneId") to scroll to when the editor opens, set when the user gets
    // here by clicking a precautionary zone marker on the map.
    @property({ attribute: false }) focusZoneKey: string | '' = '';
    // Index into _zonePoints of the crossing the review is currently on, -1 before the first step.
    @property({ attribute: false }) private _zoneCursor: number = -1;
    @property({ attribute: false }) private _reviewAcknowledged: boolean = false;
    // True while an in-editor recalculation is in flight. The editor stays open throughout.
    @property({ attribute: false }) private _recalculating: boolean = false;
    // The route's geometry as the optimiser last calculated it, as _geometryKey builds it. Only the
    // optimiser writes properties.zones, so an edit that moves the route away from this leaves the
    // reviewed crossings describing a route that no longer exists - see _geometryChangedSinceCalc.
    private _calculatedGeometryKey: string = '';
    // Whether that calculated geometry crossed precautionary zones. Deleting the zone waypoints
    // takes the crossings off the route in the editor, so by then its own waypoints no longer say it
    // ever crossed any - only this does, and it is what keeps such a route from being saved as
    // though it were an ordinary one.
    private _calculatedHadZones: boolean = false;
    private _distances = { allLength: "", pilotToPilotLength: "" }
    private _pilotOutIndex: number = -1;
    private _pilotInIndex: number = -1;
    private _debouncedUpdateEta: (eta: string, id: number) => void;
    private _debouncedUpdateEtd: (etd: string, id: number) => void;
    static get styles() {
        return [
            voyageRadioButtonsStyle,
            voyageCursorStyles,
            css`
                .container {
                    display: flex;
                    flex-direction: column;
                    min-width: 350px;
                    border: 1px solid var(--neutrals-grey-07);
                    background: var(--neutrals-grey-10);
                    font-family: 'MaerskText';
                    font-size: 14px;
                }

                .waypoints {
                    max-height: calc(100vh - 300px);
                    overflow-y: scroll;
                    margin: 5px;
                }
                
                .waypointsReviewMode {
                    max-height: calc(97vh - 300px);
                    overflow-y: scroll;
                    margin: 5px;
                }

                .waypoint {
                    display: grid;
                    margin: 5px;

                    grid-template-columns: 110px auto;
                    grid-gap: 0px;

                    border-top: 1px solid var(--neutrals-grey-06);
                    border-left: 1px solid var(--neutrals-grey-06);

                    font-family: MaerskText;
                    font-size: 12px;
                    /* line-height: 14px; */
                    color: var(--neutrals-grey-04, black);
                }

                .cell {
                    display: flex;
                    border-top: none;
                    border-left: none;
                    border-bottom: 1px solid var(--neutrals-grey-06);
                    border-right: 1px solid var(--neutrals-grey-06);
                    align-items: center;
                    padding: 11px;
                }

                .header {
                    grid-column: 1 / 3;

                    font-size: 14px;
                    user-select: none;
                    justify-content: space-between;
                    cursor: pointer;

                    padding: 5px;
                    background-color: var(--neutrals-grey-09);

                    display: grid;
                    grid-template-columns: 20px 45px 1fr 20px;
                    justify-content: start;
                }

                .header[selected] {
                    color: red;
                }

                .header[error] {
                    border: solid 1px red;
                }

                .header >* svg {
                    height: 14px;
                    transform: translate(0, 2px);
                }

                .glabel {
                    grid-column: 1;

                    justify-content: flex-end;
                    text-align: right;
                    text-transform: uppercase;
                }

                .gvalue {
                    grid-column: 2;

                    color: var(--neutrals-grey-02, black);
                    max-width: 225px;
                }

                .boolean {
                    user-select: none;
                    cursor: pointer;
                }

                .remove {
                    color: var(--waypoint-editor-remove-button-color);
                    background-color: transparent;
                    border: none;
                    cursor: pointer;
                    grid-column: 4/4;
                }

                .remove > svg {
                    width: 10px;
                    height: 10px;
                }

                .add {
                    display: none;
                }

                .add > svg {
                    width: 10px;
                    height: 10px;
                    fill: currentColor;
                    margin-right: 3px;
                }

                .addwaypoint:hover .add {
                    color: var(--primary-blue-05);
                    background: rgba(var(--primary-blue-05-rgb),0.2);
                    display: grid;
                    place-content: center;
                    grid-template-columns: auto auto;
                    width: 100%;
                    height: 16px;
                    font-size: 10px;
                    border: none;
                }

                .addwaypoint {
                    height: 16px;
                    width: 100%;
                }

                .options {
                    display: flex;
                    flex-direction: row;
                    justify-content: space-around;
                    padding: 12.5px;
                }

                .save {
                    color: white;
                    fill: currentColor;
                    height: 32px;
                    width: 156px;
                    max-width: 156px;
                    border-radius: 2.5px;
                    background-color: var(--primary-blue-05);
                }

                .save > svg {
                    height: 14px;
                }

                .undo {
                    color: var(--waypoint-editor-revert-button);
                    fill: currentColor;
                    height: 32px;
                    width: 156px;
                    border: 0;
                    background: none;
                    box-shadow: none;
                    border-radius: 0px;
                }

                .undo > svg {
                    height: 12px;
                }

                /* ---- Precautionary zone review (const_pow_endpoint_v2) ------------------ */

                /* "Show Route warnings (n): Next Previous" strip above the waypoint list */
                .warningsNav {
                    display: flex;
                    align-items: center;
                    column-gap: 10px;

                    padding: 8px 12px;
                    background-color: var(--neutrals-grey-09);
                    border-bottom: 1px solid var(--neutrals-grey-06);
                    font-size: 14px;
                    color: var(--neutrals-grey-02, black);
                }

                .warningsNavLabel {
                    margin-right: auto;
                }

                .warningsNavButton {
                    display: inline-flex;
                    align-items: center;
                    column-gap: 4px;

                    border: none;
                    background: none;
                    padding: 0;
                    color: var(--primary-blue-03, #0073ad);
                    font-family: 'MaerskText';
                    font-size: 14px;
                    text-decoration: underline;
                    cursor: pointer;
                }

                .warningsNavButton[disabled] {
                    color: var(--neutrals-grey-05, #878787);
                    text-decoration: none;
                    cursor: not-allowed;
                }

                .warningsNavButton > svg {
                    height: 12px;
                    fill: currentColor;
                }

                /* Zone waypoint header: entry rows read as a warning, exit rows as neutral. The
                   waypoint keeps all of its normal fields, they are just collapsed by default. */
                .header[zone-entry] {
                    background-color: var(--feedback-danger-background, #f8d7d5);
                }

                .header[zone-exit] {
                    background-color: var(--neutrals-grey-07, #d9d9d9);
                }

                .header[zone-focused] {
                    box-shadow: inset 3px 0 0 0 var(--primary-blue-03, #0073ad);
                }

                .zoneLabel {
                    display: inline-flex;
                    align-items: center;
                    column-gap: 6px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .zoneLabeltxt { 
                    font-size: 12px;
                }

                .zoneHelp {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 15px;
                    height: 15px;
                    flex: 0 0 auto;

                    border: 1px solid var(--neutrals-grey-04, #767676);
                    border-radius: 50%;
                    font-size: 10px;
                    font-weight: 700;
                    cursor: help;
                }

                /* "Coordinates: ... Time: ..." line under a zone waypoint header */
                .zoneMeta {
                    grid-column: 1 / 3;

                    padding: 6px 11px;
                    font-size: 12px;
                    color: var(--neutrals-grey-03, #545454);
                    border-bottom: 1px solid var(--neutrals-grey-06);
                }

                .zoneMeta > span {
                    margin-right: 12px;
                }

                /* Review-mode footer: distances on the left, revert on the right, then the two
                   actions underneath. */
                .reviewDistances {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    column-gap: 10px;
                    padding: 10px 12px 0;
                    font-size: 13px;
                    color: var(--neutrals-grey-02, black);
                }

                .reviewDistances .undo {
                    width: auto;
                    height: auto;
                    flex: 0 0 auto;
                }

                .review {
                    color: var(--primary-blue-05);
                    fill: currentColor;
                    height: 32px;
                    width: 156px;
                    max-width: 156px;
                    border-radius: 2.5px;
                    border: 1px solid var(--primary-blue-05);
                    background-color: transparent;
                    font-family: 'MaerskText';
                    cursor: pointer;
                }

                .review[disabled], .save[disabled] {
                    opacity: 0.45;
                    cursor: not-allowed;
                }

                .saveGate {
                    display: inline-flex;
                }

                .reviewGate {
                    display: inline-flex;
                }

                mc-tooltip > div:last-child {
                    max-width: 280px;
                    word-wrap: break-word;
                    line-height: 1.4;
                }

                .detailsView {
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    margin: 7px;
                    height: 41px;
                }

                .wpdetail {
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    align-items: center;
                }

                .wpdetail input[error] {
                    border-color: var(--voyage-error-red, red);
                    color: var(--voyage-error-red, red);
                }

                .toptext {
                    font-size: 12px;
                    color: var(--neutrals-grey-04, black);
                }

                .lowertext {
                    font-size: 10px;
                    color: var(--neutrals-grey-02, black);
                }

                .showAll {
                    border: 0;
                    background: transparent;
                    -webkit-appearance: none;
                    grid-column: 1/1;
                }

                .showAll > svg{
                    color: var(--neutrals-grey-04, black);
                    width: 10px;
                    height: 10px;
                }

                textarea {
                    background-color: unset;
                    resize: none;
                    font-family: MaerskText;
                    font-size: 12px;
                }

                .wpdetail > input[type="number"] {
                    width: 45px;
                    font-size: 12px;
                    color: var(--neutrals-grey-04, black);
                    text-align: center;
                }

                .wpdetail > input[type="number"]::-webkit-outer-spin-button, input::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }

                input[type="radio"] {
                    margin-left: 2px;
                    margin-right: 2px;
                }

                input {
                    border: 1px solid var(--neutrals-grey-06, grey);
                    background-color: unset;
                    font: inherit;
                }

                input[type="datetime-local"] {
                    font-size: 14px;
                }

                label[for="navigationalInput_0"],
                label[for="hardInput_0"] {
                    cursor: pointer;
                }

                .RLGCLabel {
                    display: block;
                    text-align: center;
                }

                .gvalue > label {
                    font-size: 10px;
                }

                .gvalue > label:not(:last-child) {
                    margin-right: 15px;
                }

                .gvalue > input[type="radio"] {
                    margin-left: 0px;
                    margin-right: 5px;
                }

                .wpHeaderText {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    grid-column: 2/2;
                    padding-left: 8px;
                }

                .wpTypeIndicator {
                    background-color: var(--neutrals-grey-01, black);
                    width: 6px;
                    height: 6px;
                    margin-right: 3px;
                    border-radius: 50%;
                }

                .wpLabelEdit {
                    border: 0px; 
                    grid-column: 3/3; 
                    font-size: 13px;
                }

                .navigational {
                    position: relative;
                    background-color: transparent;
                }

                .navigational:before, .navigational:after {
                    content: " ";
                    position: absolute;
                    background-color: var(--neutrals-grey-01, black);
                }

                .navigational:before {
                    width: 4px;
                    height: 10px;
                    margin-left: 1px;
                    margin-top: -2px;
                }

                .navigational:after {
                    width: 10px;
                    height: 4px;
                    margin-left: -2px;
                    margin-top: 1px;
                }

                .length {
                    font-family: 'MaerskText';
                    font-size: 12px;
                    color: var(--neutrals-grey-02, black);
                    display: grid;
                    grid-template-rows: auto auto;
                    grid-row-gap: 10px;
                    place-content: center;
                }

                .length-content {
                    display: grid;
                    grid-template-columns: 118px auto;
                    grid-column-gap: 10px;
                }

                input:disabled,textarea:disabled,voy-lat-long-input[disabled] {
                    background-color: var(--neutrals-grey-09, grey);
                }

                button:disabled {
                    opacity: 0.5;
                }

                voy-lat-long-input {
                    width: auto;
                    background-color: unset;
                    border-radius: 0;
                }

                input[type="datetime-local"][error] {
                    border: solid 1px red;
                }

                .selectors {
                    display: flex;
                    flex-direction: row;
                    margin: 7px;
                    margin-top: 0px;
                }

                .selector {
                    display: flex;
                    flex-direction: column;
                    align-items: left;
                    font-size: 10px;
                    color: var(--neutrals-grey-02);
                    flex-grow: 1;
                }

                .selector:first-child {
                    margin-right: 10px;
                }

                .savingOverlay {
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    background-color: rgba(var(--neutrals-grey-01-rgb), 0.5);
                }

                .savingOverlay[hidden] {
                    display: none;
                }

                voyage-spinner {
                    width: 75px;
                    height: 75px;
                }

                select {
                    font-family: 'MaerskText';
                    font-size: 12px;
                    border: 1px solid var(--neutrals-grey-06);
                    border-radius: 2px;
                    background: var(--neutrals-grey-10);
                    color: var(--neutrals-grey-02);
                    margin-top: 5px;
                }

                .last-waypoint {
                    display: grid;
                    grid-template-columns: 80px auto 7px;
                }
                
                .wpTypeStoppage{
                    display: flex;
                    align-items: center;
                    border-radius: 50%;
                }
                
                .stoppage {
                    display: flex;
                    align-items: center;
                    flex-direction: column;
                }

                .comment {
                    font-family: 'MaerskText';
                    width: 95%;
                    height: 35px;
                    border: 1px solid #c4c4cd;
                    line-height: 20px;
                    resize: vertical;
                    border-radius: 6px;
                }

                .comment-input{
                    width: 100%;
                    position: realtive;
                }

                .comment-input > textarea {
                    white-space: normal;
                    padding-left: 10px;
                    font-family: MaerskText;
                }
                
                .name {
                    color: var(--neutrals-grey-02, grey);
                    font-size: 14px;
                    font-family: MaerskText;
                    line-height:20px;
                    font-weight: 400;
                    margin-top: 8px;
                }
                
                .dropdown {
                    box-sizing: border-box;
                    min-height: 41px;
                    border: 1px solid var(--neutrals-grey-06, grey);
                    border-radius: 2.5px;
                    font-family: MaerskText;
                    font-size: 14px;
                    color: var(--neutrals-grey-02, black);
                    background-color: var(--neutrals-grey-10, white);
                    text-indent: 5px;
                    width: 98%;
                    margin-bottom: 10px;
                    border-radius: 6px;
                }

                .lockedStoppageMessage {
                    color: var(--voyage-error-red, red);
                    font-size: 11px;
                    line-height: 14px;
                    margin-top: 6px;
                }
        `];
    }

    connectedCallback() {
        super.connectedCallback();
        RouteEditModel!.addEventListener('route-changed', <EventListener>this._onRouteChanged);
        RouteEditModel!.addEventListener('waypoint-selected', this._onSelectionChanged);
        VesselPlanningModel!.addEventListener('vessel_characteristics_updated', this._getVesselCharacteristics)
        VesselPlanningModel!.addEventListener('reset-star-route-view', () =>this.setStarRouteLayerVisibility('', ''))
        // The flags are fetched asynchronously and refreshed periodically, so the review UI has to
        // appear (or disappear) when const_pow_endpoint_v2 shows up in a later response.
        FeatureFlagModel?.addEventListener('featureFlagsUpdated', this._onFeatureFlagsUpdated);
        this._onRouteChanged(new CustomEvent("", { detail: { waypoints: RouteEditModel!.getWaypoints() } }));


        this._distances = this._getDistances(this._waypoints.slice(0, -1));

        this._getVesselCharacteristics();
    }

    updated(changed: Map<string, unknown>) {
        super.updated(<any>changed);

        // A different route in the same element is a new edit session, so the geometry left behind by
        // the last one's recalculation says nothing about this one.
        if (changed.has('selectedRouteId')) {
            this._calculatedGeometryKey = '';
            this._calculatedHadZones = false;
        }

        // Arriving from a precautionary zone marker on the map: start the review on that crossing.
        if (changed.has('focusZoneKey') && this.focusZoneKey) {
            const index = this._zonePoints.findIndex(crossing => crossing.point.key === this.focusZoneKey);
            if (index >= 0 && index !== this._zoneCursor) {
                this._focusWarning(index);
            }
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        RouteEditModel!.removeEventListener('route-changed', <EventListener>this._onRouteChanged);
        RouteEditModel!.removeEventListener('waypoint-selected', this._onSelectionChanged);
        VesselPlanningModel!.removeEventListener('reset-star-route-view', () =>this.setStarRouteLayerVisibility('', ''))
        FeatureFlagModel?.removeEventListener('featureFlagsUpdated', this._onFeatureFlagsUpdated);
    }

    private _onFeatureFlagsUpdated = () => {
        this.requestUpdate();
    };

    /**
     * ==================================================================================
     * SAVE & EXIT HELPER: Check Route Alignment with Star Route
     * ==================================================================================
     * 
     * Checks if route is geometrically aligned with a specific star route.
     * 
     * BACKEND API:
     * \u2022 Calls: VesselPlanningModel.isAlignedRoute()
     * \u2022 Which calls: POST /bpr/adopted
     * \u2022 Payload: { route, bprID }
     * \u2022 Response: { adopted: boolean }
     * 
     * ALIGNMENT LOGIC (Backend):
     * \u2022 Compares route waypoint geometry with star route geometry
     * \u2022 Returns true if route follows star route path within acceptable deviation limits
     * \u2022 Returns false if route deviates too much from star route
     * 
     * @returns true if route geometry aligns with star route (within limits)
     */
    private async _checkRouteAlignment(route: VoyageRoute, starRouteId: string): Promise<boolean> {
        const allRoutes = VoyageRoutingModel!.routes;
        const starRoute: VoyageRoute | undefined = allRoutes.find(route => route.routeId === starRouteId);

        if (starRoute?.bestPracticeRouteId) {
            try {
                const isAligned = await VesselPlanningModel!.isAlignedRoute(route, starRoute.bestPracticeRouteId);
                return isAligned;
            } catch (error) {
                console.error('Error checking alignment:', error);
                return false;
            }
        }
        return false;
    }

    render() {
        // A route crossing precautionary zones has to be reviewed before it can be saved, so the
        // editor grows the warnings navigation and the "Mark as reviewed" gate. Routes without such
        // crossings - and everything with const_pow_endpoint_v2 disabled - render as they always did.
        const inReview = this._inWarningsReview;
        const wayPointsClass = inReview ? "waypointsReviewMode" : "waypoints";

        return html`
            <div class="container">
                ${inReview ? this._renderWarningsNav() : html``}
                <div class="${wayPointsClass}" id="waypoints-list">
                    ${this._populate()}
                </div>
                ${inReview ? this._renderReviewFooter() : html`
                    <div class="length">
                        <div class="length-content">
                            <div class="length-content-text">Total distance</div>
                            <div class="length-content-value">${this._distances.allLength} NM</div>
                        </div>
                        <div class="length-content">
                            <div class="length-content-text">Pilot to Pilot distance</div>
                            <div class="length-content-value">${this._distances.pilotToPilotLength} NM</div>
                        </div>
                    </div>
                    <div class="options">
                        <button class="undo" @click=${this._revert} ?disabled=${!RouteEditModel!.canRevert()}>${SvgIcons.undo} REVERT CHANGES</button>
                        ${this._awaitingRecalculation ? this._renderRecalculateButton(
                            `This route crossed precautionary zones and has been edited, so whether it
                             still does is unknown. Recalculate before saving.`)
                        : html`
                            <button class="save" data-test="save-and-exit" @click=${() => this._saveAndClose()}>${SvgIcons.solidSave} Save & Exit</button>
                        `}
                    </div>
                `}
                <div class="savingOverlay" ?hidden=${!this._saving && !this._recalculating}>
                    <voyage-spinner></voyage-spinner>
                </div>
            </div>
       `;
    }

    private _renderWarningsNav() {
        const points = this._zonePoints;
        const atFirst = this._zoneCursor <= 0;
        const atLast = this._zoneCursor >= points.length - 1;

        return html`
            <div class="warningsNav">
                <span class="warningsNavLabel">Show Route warnings (${points.length}):</span>
                <button class="warningsNavButton" data-test="next-warning" ?disabled=${atLast}
                        @click=${() => this._stepWarning(1)}>Next ${SvgIcons.lightChevronRight}</button>
                <button class="warningsNavButton" data-test="previous-warning" ?disabled=${atFirst}
                        @click=${() => this._stepWarning(-1)}>${SvgIcons.chevronLeft} Previous</button>
            </div>
        `;
    }

    // "Recalculate" in place of "Save & Exit", with the reason it is there in a tooltip.
    private _renderRecalculateButton(explanation: string) {
        return html`
            <mc-tooltip position="top-center">
                <div class="saveGate" slot="trigger">
                    <button class="save" data-test="recalculate" ?disabled=${this._recalculating}
                            @click=${() => this._recalculate()}>Recalculate</button>
                </div>
                <div>${explanation}</div>
            </mc-tooltip>
        `;
    }

    private _renderReviewFooter() {
        // An edit to the route's shape leaves the listed warnings describing the route as it was, so
        // the footer asks for a recalculation instead of a save until the crossings are trustworthy
        // again, and the review itself is held shut in the meantime.
        const awaitingRecalc = this._awaitingRecalculation;
        const canAcknowledge = this._allWarningsVisited && !awaitingRecalc;
        const saveBlocked = !this._reviewAcknowledged;

        const saveButton = html`
            <button class="save" data-test="save-and-exit" ?disabled=${saveBlocked}
                    @click=${() => this._saveAndClose()}>${SvgIcons.solidSave} Save & Exit</button>
        `;

        return html`
            <div class="reviewDistances">
                <div>
                    <div>Total distance: ${this._distances.allLength} NM</div>
                    <div>Pilot to Pilot distance: ${this._distances.pilotToPilotLength} NM</div>
                </div>
                <button class="undo" @click=${this._revert} ?disabled=${!RouteEditModel!.canRevert()}>${SvgIcons.undo} Revert Changes</button>
            </div>
            <div class="options">
                ${(!canAcknowledge || this._reviewAcknowledged) ? html`
                    <mc-tooltip position="top-center">
                        <div class="reviewGate" slot="trigger">
                            <button class="review" data-test="mark-as-reviewed" ?disabled=${!canAcknowledge || this._reviewAcknowledged}
                                    @click=${() => this._acknowledgeWarnings()}>Mark as reviewed</button>
                        </div>
                        <div>
                            Navigate through all route warnings<br/>using Next/Previous buttons to enable this.
                        </div>
                    </mc-tooltip>
                ` : html`
                    <button class="review" data-test="mark-as-reviewed" ?disabled=${!canAcknowledge || this._reviewAcknowledged}
                            @click=${() => this._acknowledgeWarnings()}>Mark as reviewed</button>
                `}
                ${awaitingRecalc ? this._renderRecalculateButton(
                    `The route has changed, so its Route Warnings no longer describe it. Recalculate
                     to find the precautionary zones the new route crosses.`)
                : saveBlocked ? html`
                    <mc-tooltip position="top-center">
                        <div class="saveGate" slot="trigger">${saveButton}</div>
                        <div>
                            Review all Route Warnings and click 'Mark as reviewed'<br/>to enable 'Save &amp; Exit'
                        </div>
                    </mc-tooltip>
                ` : saveButton}
            </div>
        `;
    }

    private async _getVesselCharacteristics() {
        // Set the Vessel Characteristics used by the field updaters to validate input
        const characteristics = await VesselPlanningModel!.getVesselCharacteristics();
        this._vesselCharacteristics = characteristics;
    }

    private _routeEditorValidationErrors: ValidatonErrors = new Map([
        ["eta", new Map()],
        ["etd", new Map()],
        ["min-speed", new Map()],
        ["max-speed", new Map()],
    ]);

    //True only for the second of a stoppage pair (departure row), not for the main stoppage waypoint where user sets ETD.
    private _isHiddenStoppageEndRow(idx: number): boolean {
        const wp = this._waypoints[idx];
        if (wp.properties.stoppage_end !== true) return false;
        const prev = idx > 0 ? this._waypoints[idx - 1] : null;
        return prev?.properties?.is_stoppage_point === true;
    }

    private _getStoppageEndIndex(idx: number): number {
        const wp = this._waypoints[idx];
        const nextIdx = idx + 1;
        if (wp?.properties?.is_stoppage_point !== true || wp?.properties?.stoppage_end === true) {
            return -1;
        }
        if (nextIdx >= this._waypoints.length) {
            return -1;
        }

        const nextWp = this._waypoints[nextIdx];
        return nextWp?.properties?.is_stoppage_point === true && nextWp?.properties?.stoppage_end === true
            ? nextIdx
            : -1;
    }

    private _getLegWaypointIndex(idx: number): number {
        const stoppageEndIndex = this._getStoppageEndIndex(idx);
        return stoppageEndIndex >= 0 ? stoppageEndIndex : idx;
    }

    private _getNextVisibleWaypointIndex(idx: number): number {
        let nextIdx = idx + 1;
        while (nextIdx < this._waypoints.length && this._isHiddenStoppageEndRow(nextIdx)) {
            nextIdx++;
        }
        return nextIdx < this._waypoints.length ? nextIdx : -1;
    }

    private _cloneStoppageEndWaypoint(idx: number): VoyageRoutePointFeature {
        const stoppageEndWaypoint = JSON.parse(JSON.stringify(this._waypoints[idx])) as VoyageRoutePointFeature;
        stoppageEndWaypoint.properties.stoppage_end = true;
        stoppageEndWaypoint.properties.is_stoppage_point = true;
        stoppageEndWaypoint.properties.type = "Hard";
        stoppageEndWaypoint.properties.showAll = false;
        return stoppageEndWaypoint;
    }

    private _syncStoppagePair(idx: number): void {
        const stoppageEndIndex = this._getStoppageEndIndex(idx);
        if (stoppageEndIndex < 0) {
            return;
        }

        const source = this._waypoints[idx];
        const target = this._waypoints[stoppageEndIndex];
        target.geometry.coordinates = [...source.geometry.coordinates];
        target.properties.type = "Hard";
        target.properties.is_stoppage_point = true;
        target.properties.stoppage_end = true;
        target.properties.eta = source.properties.eta;
        target.properties.etd = source.properties.etd;
        target.properties.leg_type = source.properties.leg_type;
        target.properties.stoppageReason = source.properties.stoppageReason;
        target.properties.comment = source.properties.comment;
        target.properties.name = source.properties.name;
        target.properties.isPassed = source.properties.isPassed;
    }

    private _ensureStoppagePair(idx: number): void {
        if (this._getStoppageEndIndex(idx) < 0) {
            this._waypoints.splice(idx + 1, 0, this._cloneStoppageEndWaypoint(idx));
        }
        this._syncStoppagePair(idx);
    }

    private _removeStoppagePair(idx: number): void {
        const stoppageEndIndex = this._getStoppageEndIndex(idx);
        if (stoppageEndIndex >= 0) {
            this._waypoints.splice(stoppageEndIndex, 1);
        }
    }

    private _isCurrentStoppagePoint(idx: number): boolean {
        const wp = this._waypoints[idx];
        if (wp?.properties?.is_stoppage_point !== true) {
            return false;
        }

        const eta = wp.properties.eta ? new Date(wp.properties.eta).getTime() : NaN;
        const etd = wp.properties.etd ? new Date(wp.properties.etd).getTime() : NaN;
        const now = Date.now();

        return !isNaN(eta) && !isNaN(etd) && eta < now && now < etd;
    }

    private _getDisplayNumbers(): (number | null)[] {
        return getWaypointDisplayNumbers(
            this._waypoints,
            getWaypointDisplayStartIndex(this._vesselCharacteristics?.ecdisWPStartIndex)
        );
    }

    private _renderRow(wp: VoyageRoutePointFeature, idx: number, displayNumber: number | null) {
        if (this._isHiddenStoppageEndRow(idx)) {
            return html``;
        }

        const coordinates = wp.geometry.coordinates;
        const legWaypointIndex = this._getLegWaypointIndex(idx);
        const legWaypoint = this._waypoints[legWaypointIndex];
        const nextVisibleWaypointIndex = this._getNextVisibleWaypointIndex(idx);
        const isCurrentStoppagePoint = this._isCurrentStoppagePoint(idx);

        const showAll: boolean | undefined = wp.properties.showAll;
        const openWaters = this._getOpenWaters(legWaypoint, legWaypointIndex) ? "true" : "false";
        const waypointNumber = displayNumber!;
        const isWaypointValid = this._checkWaypointValidity(waypointNumber)

        const min_depth_value =
            wp.properties.userInput?.min_depth?.toFixed(1) ??
            wp.properties.min_depth?.toFixed(1) ??
            "";

        // A waypoint that opens or closes a precautionary zone behaves like any other - it keeps
        // every field and can be deleted - but its header is colour coded and shows the zone's own
        // name and transition (e.g. "Strait Of Gibraltar - Entry") in place of the free-text name.
        const zoneCrossings = this._zoneCrossingsAtWaypoint(idx);
        const zoneCrossing = zoneCrossings[0];
        const isZoneEntry = zoneCrossings.some(crossing => crossing.point.type === 'entry');
        const isZoneFocused = zoneCrossings.length > 0 && idx === this._focusedZoneWaypointIndex;
        const zoneLabel = zoneCrossings.map(crossing => crossing.point.label).join(' / ');
        const zoneLabelClipped = zoneLabel.length > ZoneLabelMaxChars;
        const zoneLabelShown = zoneLabelClipped
            ? `${zoneLabel.slice(0, ZoneLabelMaxChars).trimEnd()}...`
            : zoneLabel;

        return html`
            <div class="waypoint" id=${idx}>
                <span class="cell header" ?selected=${idx === RouteEditModel!.selectedWaypointIndex} @click=${() => { RouteEditModel!.selectedWaypointIndex = idx }} ?error=${!isWaypointValid}
                      ?zone-entry=${!!zoneCrossing && isZoneEntry} ?zone-exit=${!!zoneCrossing && !isZoneEntry} ?zone-focused=${isZoneFocused}>
                    <button class="showAll" @click=${() => this._toggleShowAll(idx)}>${showAll ? html`${SvgIcons.lightChevronUp}` : html`${SvgIcons.lightChevronRight}`}</button>
                    <div class="wpHeaderText">
                        ${wp.properties.is_stoppage_point === true ? html `<div class="wpTypeStoppage"> ${SvgIcons.minusCircle} </div>` :
                            html`<div class=${`wpTypeIndicator ${wp.properties.type === "Navigational" ? "navigational" : ""}`}></div>`}
                        <div>${displayNumber} ${wp.properties.pilotPoint ? SvgIcons.customPilotIcon : ""}</div>
                    </div>
                    ${zoneCrossing ? html`
                    <span class="zoneLabel">
                        ${zoneLabelClipped ? html`
                        <mc-tooltip position="top" appearance="inverse">
                            <span data-test="zone-label" class="zoneLabeltxt" slot="trigger">${zoneLabelShown}</span>
                            <span>${zoneLabel}</span>
                        </mc-tooltip>
                        ` : html`<span data-test="zone-label" class="zoneLabeltxt">${zoneLabelShown}</span>`}
                        <mc-tooltip position="top" appearance="inverse">
                            <span class="zoneHelp" slot="trigger" @click=${(e: Event) => e.stopPropagation()}>?</span>
                            <span>${getZoneDescription(zoneCrossing.point)}</span>
                        </mc-tooltip>
                    </span>
                    ` : html`
                    <input type="text" placeholder="Type Waypoint Details here..." .value=${wp.properties.name ? wp.properties.name : ""}
                    .disabled=${!!wp.properties.isPassed}
                    @input=${(e: InputEvent) => this._updateDetails(e, idx)} class="wpLabelEdit">
                    `}

                    ${this._waypoints.length > 2 ?
                html`<button class="remove" .disabled=${!!wp.properties.isPassed} @click=${() => this._delete(idx)}>${SvgIcons.lightTrashAlt}</button>`
                : html``}

                </span>

                ${zoneCrossing ? html`
                <div class="zoneMeta" data-test="zone-meta">
                    <span>Time: ${formatZoneTime(wp.properties.eta || wp.properties.timestamp)}</span>
                </div>
                ` : html``}

                ${showAll ? html`
                <span class="cell glabel">Pilot station</span>
                <div class="cell gvalue">
                    <voy-toggle-button .checked=${!!wp.properties.pilotPoint} .disabled=${!!wp.properties.isPassed || isCurrentStoppagePoint}
                                       @input=${(evt: InputEvent) => this._setPilot(idx, evt)}>
                    </voy-toggle-button>
                </div>

                <span class="cell glabel">WP type</span>
                <div class="cell gvalue">
                    <input type="radio" id="hardInput_${idx}" name="wptype_${idx}"
                           .checked=${wp.properties.type === "Hard" && wp.properties.is_stoppage_point !== true} .disabled=${!!wp.properties.pilotPoint || !!wp.properties.isPassed || isCurrentStoppagePoint}
                           @change=${(e: Event) => this._setWPType(e, idx, "Hard")}>
                    <label for="hardInput_${idx}">Hard</label>
                    <input type="radio" id="navigationalInput_${idx}" name="wptype_${idx}"
                           .checked=${wp.properties.type === "Navigational"} .disabled=${!!wp.properties.pilotPoint || !!wp.properties.isPassed || isCurrentStoppagePoint}
                           @change=${(e: Event) => this._setWPType(e, idx, "Navigational")}>
                    <label for="navigationalInput_${idx}">Navigational</label>
                    <input type="radio" id="stoppageInput_${idx}" name="wptype_${idx}"
                           .checked=${wp.properties.type === "Hard" && wp.properties.is_stoppage_point === true} ?disabled=${wp.properties.pilotPoint || wp.properties.isPassed || isCurrentStoppagePoint}
                           @change=${(e: Event) => this._setWPType(e, idx, "Hard", true)}>
                    <label for="navigationalInput_${idx}">Stoppage</label>
                </div>

                <span class="cell glabel">WP passed</span>
                <div class="cell gvalue">
                    <voy-toggle-button .checked=${!!wp.properties.isPassed}
                                       @input=${(evt: InputEvent) => this._setIsPassed(idx, evt)}>
                    </voy-toggle-button>
                </div>
                <span class="cell glabel">Latitude</span>
                <voy-lat-long-input
                    class="cell gvalue"
                    .value=${coordinates[1]}
                    .disabled=${!!wp.properties.isPassed}
                    @input=${(e: InputEvent) => this._updateLat(e, idx)}></voy-lat-long-input>

                <span class="cell glabel">Longitude</span>
                <voy-lat-long-input long
                    class="cell gvalue"
                    .value=${coordinates[0]}
                    .disabled=${!!wp.properties.isPassed}
                    @input=${(e: InputEvent) => this._updateLon(e, idx)}></voy-lat-long-input>

                <span class="cell glabel">
                    ETA (UTC)
                    <button class="remove" .disabled=${!!wp.properties.isPassed || isCurrentStoppagePoint} @click=${() => this._deleteETA(idx)}>X</button>
                </span>
                <input type="datetime-local"
                    id="eta-input"
                    class="cell gvalue"
                    .value=${this._updateInputWithDate(wp.properties.eta) ?? ''}
                    .disabled=${!!wp.properties.isPassed || isCurrentStoppagePoint}
                    @change=${(e: Event) => {
                    const eta = (<HTMLInputElement>e.target).value;
                    this._debouncedUpdateEta(eta, idx)
                }} 
                    ?error=${this._routeEditorValidationErrors.get("eta")?.get(waypointNumber)}
                />

                                
                ${wp.properties.is_stoppage_point === true ? html`
                    <span class="cell glabel">
                    ETD (UTC)
                    <button class="remove" .disabled=${!!wp.properties.isPassed} @click=${() => this._deleteETD(idx)}>X</button>
                    </span>
                    <input type="datetime-local"
                        id="etd-input"
                        class="cell gvalue"
                        .value=${this._updateInputWithDate(wp.properties.etd) ?? ''}
                        .disabled=${!!wp.properties.isPassed}
                        @change=${ (e: Event) => {
                            const etd = (<HTMLInputElement>e.target).value;
                            this._debouncedUpdateEtd(etd, idx)
                        }} 
                        ?error=${this._routeEditorValidationErrors.get("etd")?.get(waypointNumber)}
                    />
                    
                 ` : html  ``}
                
                `: html``}
            </div>
            ${nextVisibleWaypointIndex >= 0 ? html`
            <div class="detailsView">
                <div class="wpdetail">
                    <div class="toptext">${legWaypoint.properties.course === undefined ? '' : legWaypoint.properties.course.toFixed(0)} °</div>
                    <div class="lowertext">Course</div>
                </div>
                <div class="wpdetail">
                    <div class="toptext">${legWaypoint.properties.distance === undefined ? '' : legWaypoint.properties.distance.toFixed(0)} NM</div>
                    <div class="lowertext">Distance</div>
                </div>
                <div class="wpdetail">
                    <input type="number" min="0" class="minspeed" value=${legWaypoint.properties.speed_minimum !== undefined ? (legWaypoint.properties.speed_minimum * MpsToKts).toFixed(1) : ""}
                           .disabled=${!!wp.properties.isPassed}
                           ?error=${this._routeEditorValidationErrors.get("min-speed")?.get(waypointNumber)}
                           @change=${(e: Event) => this._updateMinSpeed(e, idx)}/>
                    <div class="lowertext">Min. speed</div>
                </div>
                <div class="wpdetail">
                    <input type="number" class="maxspeed" value=${legWaypoint.properties.speed_maximum !== undefined ? (legWaypoint.properties.speed_maximum * MpsToKts).toFixed(1) : ""}
                           .disabled=${!!wp.properties.isPassed}
                           ?error=${this._routeEditorValidationErrors.get("max-speed")?.get(waypointNumber)}
                           min=0.1
                           @change=${(e: Event) => this._updateMaxSpeed(e, idx)}/>
                    <div class="lowertext">Max. speed</div>
                </div>
                <div class="wpdetail">
                    <input type="number" min="0" class="waterdepth" value=${min_depth_value}
                           .disabled=${!!wp.properties.isPassed}
                           @change=${(e: Event) => this._updateWaterDepth(e, idx)}/>
                    <div class="lowertext">Water depth</div>
                </div>
            </div>

            <div class="selectors">
                <div class="selector">
                    <div>Leg Type</div>
                    <select .disabled=${!!wp.properties.isPassed} .value=${legWaypoint.properties.leg_type}
                            @input=${(e: InputEvent) => this._setLegType(idx, (<HTMLSelectElement>e.target).value)}>
                        <option value="Rhumbline">RL - Rhumb Line</option>
                        <option value="GreatCircle">GC - Great Circle</option>
                    </select>
                </div>
                <div class="selector">
                    <div>Squat</div>
                    <select .disabled=${!!wp.properties.isPassed} .value=${openWaters}
                            @input=${(e: InputEvent) => this._setOpenWaters(idx, (<HTMLSelectElement>e.target).value === "true")}>
                        <option value="true">O - Open Water</option>
                        <option value="false">C - Confined Water</option>
                    </select>
                </div>
            </div>
            ${wp.properties.is_stoppage_point === true ? html `
                 <div class="stoppage">
                        <div class="comment-input">
                            <div class="name">Select reason for stoppage waypoint</div>
                            <select
                                class="dropdown"
                                .value=${wp.properties.stoppageReason ?? ''} 
                                @change=${(e:InputEvent) => this._onSelectReason(e,idx)}
                            >
                                <option value="" selected disabled hidden>Select reason</option>
                                ${Object.values(stoppageWaypointReason).map(reason => {
                                    return html`<option value=${reason} ?selected=${wp.properties.stoppageReason === reason}>${reason}</option>`
                                })}
                            </select>
                        </div>
                        <div class="comment-input">
                            <div class="name">
                                Comment
                            </div>
                            <textarea
                                class="comment"
                                type="text"
                                placeholder="Please provide a comment for selected reason"
                                @input=${(e:InputEvent) => this._onInputComment(e,idx)}
                            >${wp.properties.comment ?? ''}</textarea>
                        </div>
                    </div>
                ` : html ``}
            ` : html``}

            ${nextVisibleWaypointIndex >= 0 && !this._waypoints[nextVisibleWaypointIndex].properties.isPassed ? html`
            <div class="addwaypoint">
                <button class="add" @click=${() => this._add(idx)}>${SvgIcons.plusSquare} ADD WAYPOINT</button>
            </div>
            ` : html`
            <div class="last-waypoint">
                <div class="wpdetail">
                    <div class="lowertext">Water depth</div>
                    <input type="number" min="0" class="waterdepth" value=${min_depth_value}
                        .disabled=${!!wp.properties.isPassed}
                        @change=${(e: Event) => this._updateWaterDepth(e, idx)}/>
                </div>
                <div class="selector">
                    <div>Squat</div>
                    <select .disabled=${!!wp.properties.isPassed} .value=${openWaters}
                            @input=${(e: InputEvent) => this._setOpenWaters(idx, (<HTMLSelectElement>e.target).value === "true")}>
                        <option value="true">O - Open Water</option>
                        <option value="false">C - Confined Water</option>
                    </select>
                </div>
            </div>
            `}
        `;
    }

    /**
     * ==================================================================================
     * ROUTE WARNINGS REVIEW (const_pow_endpoint_v2 only)
     * ==================================================================================
     *
     * A route that crosses precautionary (bpr_nogo) zones cannot be saved until the user has
     * stepped through every entry/exit waypoint with Next/Previous and confirmed with "Mark as
     * reviewed". Save & Exit stays disabled until then, and the acknowledgement rides along on the
     * saved route as route_info.routeWarningsReviewed, which is what unlocks "Select route" and
     * "Passage Plan" back on the routes list.
     *
     * The zone waypoints come from the optimiser on the already calculated route being edited, so
     * the review happens before saving. Routes with no such crossings keep the old editor exactly.
     */
    // Read from the feature flags response every render, so a flag that arrives (or is withdrawn)
    // after the editor opened is picked up - connectedCallback re-renders on 'featureFlagsUpdated'.
    private get _constPowEndpointV2Enabled(): boolean {
        return FeatureFlagModel?.hasFeatureFlag(CONST_POW_ENDPOINT_V2) === true;
    }

    private get _inWarningsReview(): boolean {
        return this._constPowEndpointV2Enabled && this._zonePoints.length > 0;
    }

    // The route's precautionary zone entry/exit points, in route order, numbered by crossing.
    private get _zonePoints(): ZoneCrossingItem[] {
        return this._zoneCrossings;
    }

    // Every crossing has to have been brought into view before the review can be confirmed, so
    // jumping straight to one of them (from a map marker) is not enough to unlock the button.
    private get _allWarningsVisited(): boolean {
        const points = this._zonePoints;
        return points.length > 0 && points.every(crossing => this._visitedZoneKeys.has(crossing.point.key));
    }

    // Crossings sitting on a waypoint row. One waypoint can close a zone and open the next, so it
    // can carry more than one.
    private _zoneCrossingsAtWaypoint(idx: number): ZoneCrossingItem[] {
        const waypoint = this._waypoints[idx];
        if (!this._inWarningsReview || !waypoint) {
            return [];
        }

        const coordKey = wpCoordKey(waypoint.geometry.coordinates);
        return this._zonePoints.filter(crossing => wpCoordKey(crossing.point.coordinates) === coordKey);
    }

    private get _focusedZoneWaypointIndex(): number {
        const crossing = this._zonePoints[this._zoneCursor];
        return crossing ? this._waypointIndexOfZonePoint(crossing.point) : -1;
    }

    private _waypointIndexOfZonePoint(point: ZonePoint): number {
        const coordKey = wpCoordKey(point.coordinates);
        return this._waypoints.findIndex((wp, idx) =>
            !this._isHiddenStoppageEndRow(idx) && wpCoordKey(wp.geometry.coordinates) === coordKey);
    }

    // Moves the review one crossing forward or back and scrolls its waypoint into view.
    private async _stepWarning(direction: 1 | -1) {
        const points = this._zonePoints;
        // The first "Next" lands on the first crossing rather than the second.
        const from = this._zoneCursor < 0 && direction === 1 ? -1 : this._zoneCursor;
        const target = from + direction;
        if (target < 0 || target >= points.length) {
            return;
        }

        await this._focusWarning(target);
    }

    private async _focusWarning(index: number) {
        const points = this._zonePoints;
        if (index < 0 || index >= points.length) {
            return;
        }

        this._zoneCursor = index;
        this._visitedZoneKeys.add(points[index].point.key);
        this.dispatchEvent(new CustomEvent('zone-point-focused', {
            detail: { key: points[index].point.key },
            bubbles: true,
            composed: true,
        }));

        await this.updateComplete;

        const waypointIndex = this._waypointIndexOfZonePoint(points[index].point);
        if (waypointIndex < 0) {
            return;
        }

        const row = this.shadowRoot!.getElementById(`${waypointIndex}`);
        const list = this.shadowRoot!.getElementById('waypoints-list');
        if (row && list) {
            ScrollTo(list, (row.offsetTop - list.offsetTop) - 20, 500);
        }
    }

    private _acknowledgeWarnings() {
        if (!this._allWarningsVisited || this._awaitingRecalculation) {
            return;
        }
        this._reviewAcknowledged = true;
    }

    /**
     * ----------------------------------------------------------------------------------
     * RECALCULATION AFTER A GEOMETRY EDIT (const_pow_endpoint_v2 only)
     * ----------------------------------------------------------------------------------
     *
     * The warnings review reads properties.zones, which only the optimiser ever writes. So the
     * moment the user changes the route's shape the listed crossings go stale: waypoints that moved
     * still carry the zones of where they used to be, and inserted ones carry none at all. Nothing
     * in the editor can work out the new crossings - only a recalculation can.
     *
     * While the route sits in that state the footer offers "Recalculate" in place of "Save & Exit",
     * and "Mark as reviewed" is held shut. The recalculation happens without leaving the editor and
     * without saving anything, and its result decides what comes next: a route that still crosses
     * precautionary zones goes back into the review, one that no longer does falls through to the
     * ordinary footer.
     */
    // The route's shape, as the parts of it that decide which zones it crosses: where the waypoints
    // are, and the leg type that decides the path drawn between them. Deliberately not the editable
    // fields (name, ETA, speeds...) - those cannot move the route.
    private _geometryKey(waypoints: VoyageRoutePointFeature[]): string {
        return waypoints
            .map(wp => `${wp.geometry.coordinates[0]},${wp.geometry.coordinates[1]}:${wp.properties.leg_type ?? ''}`)
            .join('|');
    }

    // The route's shape as the optimiser last produced it: the saved route the editor was opened on,
    // or the result of the last in-editor recalculation once there has been one. Anchoring on the
    // saved route rather than on whatever the editor happened to open with keeps this honest if the
    // element outlives one edit session, and matches where _reviewedCrossingKeys reads from.
    private _lastCalculatedGeometryKey(): string {
        if (this._calculatedGeometryKey !== '') {
            return this._calculatedGeometryKey;
        }

        const route = VoyageRoutingModel!.routes.find(candidate => candidate.routeId === this.selectedRouteId);
        if (!route?.route_geometry?.length) {
            return '';
        }

        return this._geometryKey(getWaypointsFromRoute(route.route_geometry).filter(filterConditionNavigational));
    }

    private get _geometryChangedSinceCalc(): boolean {
        const lastCalculated = this._lastCalculatedGeometryKey();
        return lastCalculated !== '' && this._geometryKey(this._waypoints) !== lastCalculated;
    }

    // Whether the last calculated route crossed precautionary zones, which is not the same question
    // as whether the route in the editor does: editing the zone waypoints away answers the second one
    // "no" while leaving the first one "yes".
    private _lastCalculatedHadZones(): boolean {
        if (this._calculatedGeometryKey !== '') {
            return this._calculatedHadZones;
        }

        const route = VoyageRoutingModel!.routes.find(candidate => candidate.routeId === this.selectedRouteId);
        if (!route?.route_geometry?.length) {
            return false;
        }

        return getZoneCrossingItems(getEntryExitPoints(route.route_geometry as any[])).length > 0;
    }

    /**
     * Whether the route has to be recalculated before it can be saved.
     *
     * True for an edited route that has anything to do with precautionary zones - either it still
     * shows crossings, or the route it was calculated from did. The second half is what catches a
     * route whose zone waypoints have just been deleted: the editor drops out of the review because
     * there are no crossings left to list, but only the optimiser can say whether the new shape
     * really clears the zones, so it must not fall through to an ordinary Save & Exit.
     *
     * Routes that never crossed a zone are untouched by any of this and save as they always did.
     */
    private get _awaitingRecalculation(): boolean {
        return this._constPowEndpointV2Enabled
            && this._geometryChangedSinceCalc
            && (this._zonePoints.length > 0 || this._lastCalculatedHadZones());
    }

    /**
     * Recalculates the edited route in place: the user stays in the editor and nothing is written to
     * the plan, which only happens if they go on to Save & Exit.
     *
     * Feeding the calculated geometry back through RouteEditModel is what makes the flow switch by
     * itself - _onRouteChanged runs _refreshZoneCrossings over the optimiser's fresh properties.zones,
     * and _inWarningsReview is derived from those crossings. If the new route still crosses zones the
     * review comes back with the new crossings unvisited, so the user steps through them before
     * "Mark as reviewed" unlocks "Save & Exit"; if it crosses none, the review disappears.
     */
    private async _recalculate(): Promise<void> {
        if (this._recalculating || !this.plan) {
            return;
        }

        this._recalculating = true;
        try {
            // Building the route object rebuilds the geometry from the edited waypoints, which throws
            // on a route too short to be one - so it belongs inside the same guard as the call.
            const routeObject = this._getOrCreateRouteObject();
            const options: RoutingJobOptions = {
                routeId: routeObject.routeId,
                uiRouteIndex: RouteEditModel!.uiRouteIndex,
                planScheduleId: this.plan.scheduleId,
                draftFore: this.plan.foreDraft,
                draftAft: this.plan.aftDraft,
                gmt: this.plan.gmt !== null ? this.plan.gmt : undefined,
                etd: this.plan.etd,
                eta: this.plan.eta,
                rollAngle: this.plan.rollAngle !== null ? this.plan.rollAngle : undefined,
                numberOfReefers: this.plan.numberOfReefers,
                routeType: "WeatherOptimized",
                arrivalPilotEta: this.plan.arrivalPilotEta,
                departurePilotEtd: this.plan.departurePilotEtd,
                passagePlan: routeObject.passagePlan,
                weatherRoutingInfo: routeObject.weatherRoutingInfo,
                solidGm: this.plan.solidGm !== null ? this.plan.solidGm : undefined,
                // No updatePlan: this recalculation must not touch the saved plan.
            };

            if (routeObject.route_info) {
                options.whaleZonesApplied = routeObject.route_info.whaleZonesApplied;
                options.disableWhaleZoneSpeedRestrictions = routeObject.route_info.disableWhaleZoneSpeedRestrictions;
            }

            const calculatedRoute = await VoyageRoutingModel!.postRoutingJobUsingRoute(routeObject, options);

            // Offline on the cloud build the calculation is skipped and an empty route comes back
            // rather than an error, so an absent geometry is a failure like any other.
            if (!calculatedRoute?.route_geometry?.length) {
                VoyageDialogPopupManager.createDialog({
                    headerText: "Route not recalculated",
                    bodyText: "The route could not be recalculated, so its route warnings are unchanged.\nCheck the connection and try again.",
                    acceptButtonText: "Got it"
                });
                return;
            }

            RouteEditModel!.setNewRoute(getWaypointsFromRoute(calculatedRoute.route_geometry));
            // setNewRoute has already run _refreshZoneCrossings over the optimiser's fresh zones, so
            // _zoneCrossings describes the route this calculation produced.
            this._calculatedGeometryKey = this._geometryKey(this._waypoints);
            this._calculatedHadZones = this._zoneCrossings.length > 0;
        } catch (error) {
            console.error('Error recalculating route:', error);
            VoyageDialogPopupManager.createDialog({
                headerText: "Recalculation failed",
                bodyText: "The route could not be recalculated, so its route warnings are unchanged.\nPlease try again.",
                acceptButtonText: "Got it"
            });
        } finally {
            this._recalculating = false;
        }
    }

    // Recomputes the crossings whenever the edited route changes, and keeps the review honest: if an
    // edit changes which zones are crossed the acknowledgement is dropped, since the user has not
    // seen the new set. A crossing count that stays the same keeps the progress made so far.
    private _refreshZoneCrossings() {
        const previousKeys = this._zoneCrossings.map(crossing => crossing.point.key).join('|');
        this._zoneCrossings = getZoneCrossingItems(getEntryExitPoints(this._waypoints as any[]));
        const currentKeys = this._zoneCrossings.map(crossing => crossing.point.key).join('|');

        if (previousKeys === currentKeys) {
            return;
        }

        // A route that was reviewed earlier opens with its review intact - the acknowledgement is
        // persisted on the route, so coming back through "Edit route" or "(View more)" must not ask
        // for it again. It only counts while the route still crosses exactly the reviewed points.
        if (currentKeys !== '' && currentKeys === this._reviewedCrossingKeys()) {
            this._reviewAcknowledged = true;
            this._visitedZoneKeys = new Set(this._zoneCrossings.map(crossing => crossing.point.key));
            this._zoneCursor = -1;
            return;
        }

        this._reviewAcknowledged = false;
        this._visitedZoneKeys = new Set();
        this._zoneCursor = -1;
    }

    // The crossings acknowledged on the saved route, as the same key list _refreshZoneCrossings
    // builds, or '' when the route has no acknowledgement to carry into this session.
    private _reviewedCrossingKeys(): string {
        const route = VoyageRoutingModel!.routes.find(r => r.routeId === this.selectedRouteId);
        if (!routeWarningsReviewed(route)) {
            return '';
        }

        return getZoneCrossingItems(getEntryExitPoints(route!.route_geometry as any[]))
            .map(crossing => crossing.point.key).join('|');
    }

    private _zoneCrossings: ZoneCrossingItem[] = [];
    // Keys of the crossings the user has brought into view during this review.
    private _visitedZoneKeys: Set<string> = new Set();

    private _getOpenWaters(waypoint: VoyageRoutePointFeature, index: number): boolean {
        if (waypoint.properties.openWaters !== undefined) {
            return waypoint.properties.openWaters;
        }

        // The default value depends on which part of the route we are on
        // (berth -> pilot, pilot -> pilot or pilot -> berth)
        if (index >= this._pilotOutIndex && (this._pilotInIndex < 0 || index < this._pilotInIndex)) {
            return true;
        } else {
            return false;
        }
    }

    private _onInputComment = (evt: InputEvent, id: number) => {
        const inputElement = evt.target as HTMLTextAreaElement;
        let comment: string | undefined;
        comment = inputElement.value.trim();
        const wp = this._waypoints[id];
        wp.properties.comment = comment;
        this._syncStoppagePair(id);

        RouteEditModel!.setNewRoute(this._waypoints);
    };

    private _onSelectReason(evt: InputEvent, id: number) {
        const inputElement = evt.target as HTMLTextAreaElement;
        const selectedReason = inputElement.value;
        const wp = this._waypoints[id];
    
        if (wp.properties.stoppageReason !== selectedReason) {
            wp.properties.stoppageReason = selectedReason;
            this._syncStoppagePair(id);
            RouteEditModel!.setNewRoute(this._waypoints);
        }
    }

    private _setOpenWaters(id: number, openWaters: boolean) {
        this._waypoints[this._getLegWaypointIndex(id)].properties.openWaters = openWaters;
        RouteEditModel!.setNewRoute(this._waypoints);
    }

    private _toggleShowAll(id: number) {
        const wp = this._waypoints[id];
        wp.properties.showAll = !wp.properties.showAll;
        RouteEditModel!.setNewRoute(this._waypoints);
    }

    private _setLegType(id: number, type: string) {
        const legWaypointIndex = this._getLegWaypointIndex(id);
        this._waypoints[id].properties.leg_type = type;
        if (legWaypointIndex !== id) {
            this._waypoints[legWaypointIndex].properties.leg_type = type;
        }
        RouteEditModel!.setNewRoute(this._waypoints);
    }

    private _setWPType(e: Event, id: number, type: "Hard"|"Navigational", isStoppage = false) {
        const target = <HTMLInputElement>e.target;
        if (this._isCurrentStoppagePoint(id)) {
            target.checked = isStoppage;
            this.requestUpdate();
            return;
        }

        if (target.checked) {
            this._waypoints[id].properties.type = type;
            if (type === "Navigational") {
                this._waypoints[id].properties.userInserted = true;
            }
            if (isStoppage) {
                this._waypoints[id].properties.etd = this._waypoints[id].properties.etd || ''; 
                this._waypoints[id].properties.is_stoppage_point = true;
                this._ensureStoppagePair(id);
            } else {
                this._removeStoppagePair(id);
                delete this._waypoints[id].properties.etd;
                delete this._waypoints[id].properties.is_stoppage_point;
                delete this._waypoints[id].properties.stoppageReason;
                delete this._waypoints[id].properties.comment;
            }
            this._validateStoppageTimings();
            RouteEditModel!.setNewRoute(this._waypoints);
        }
    }

    private _setPilot(id: number, evt: InputEvent) {
        const wp = this._waypoints[id];
        const target = <VoyToggleButton>evt.target;
        if (this._isCurrentStoppagePoint(id)) {
            target.checked = !!wp.properties.pilotPoint;
            return;
        }
        
        wp.properties.pilotPoint = target.checked;
        if(wp.properties.type === "Hard" && wp.properties.etd !== undefined) {
            this._removeStoppagePair(id);
            wp.properties.type = undefined;
            wp.properties.etd = undefined; 
            wp.properties.stoppageReason = undefined;
            wp.properties.comment = undefined;
            wp.properties.is_stoppage_point = undefined;
        }
        if (wp.properties.pilotPoint && wp.properties.type != "Hard") {
            wp.properties.type = "Hard";
        }
        if (!wp.properties.pilotPoint){
            wp.properties.eta = undefined;
        }
        this._validateStoppageTimings();
        RouteEditModel!.setNewRoute(this._waypoints);
    }

    private _setIsPassed(id: number, evt: InputEvent) {
        const target = <VoyToggleButton>evt.target;

        if (target.checked) {
            // Set isPassed on this waypoint and all earlier waypoints
            const lastPassedIndex = this._getStoppageEndIndex(id) >= 0 ? this._getStoppageEndIndex(id) : id;
            for (let i = 0; i <= lastPassedIndex; i++) {
                this._waypoints[i].properties.isPassed = true;
            }
        } else {
            // Clear isPassed on this waypoint and all later waypoints
            for (let i = id; i < this._waypoints.length; i++) {
                delete this._waypoints[i].properties.isPassed;
            }
        }

        RouteEditModel!.setNewRoute(this._waypoints);
    }

    private _updateDetails(evt: InputEvent, id: number) {
        const wp = this._waypoints[id];

        if (wp.properties.name !== (<HTMLTextAreaElement>evt.target!).value) {
            wp.properties.name = (<HTMLTextAreaElement>evt.target!).value;
            this._syncStoppagePair(id);
            RouteEditModel!.setNewRoute(this._waypoints);
        }
    }

    private _checkWaypointValidity(waypoint: number): boolean {
        let isValid = true;

        for (const [_, map] of this._routeEditorValidationErrors.entries()) {
            if (isValid === false) break;
            isValid = !map.has(waypoint);
        }

        return isValid;
    }

    private _checkRouteValidity(): boolean {
        let isValid = true;

        for (const [_, map] of this._routeEditorValidationErrors.entries()) {
            if (isValid === false) break;
            isValid = map.size === 0;
        }

        return isValid;
    }

    private _setFieldValidationError(type: ValidatoErrorType, waypoint: number, message: string): void {
        this._routeEditorValidationErrors.get(type)?.set(waypoint, message)
    }

    private _deleteValidationError(type: ValidatoErrorType, waypoint: number): void {
        this._routeEditorValidationErrors.get(type)?.delete(waypoint)
    }

    private _clearAllValidationErrors(): void {
        this._routeEditorValidationErrors.forEach((map) => map.clear());
    }

    private _getPilotTimingEntries(): { displayNumber: number; timestamp: number }[] {
        if (!this.plan) {
            return [];
        }

        const pilotPointIndices = this._findAllPilotPointIndices();
        const displayNumbers = this._getDisplayNumbers();

        return pilotPointIndices.reduce<{ displayNumber: number; timestamp: number }[]>((entries, waypointIndex, pilotIndex) => {
            const waypoint = this._waypoints[waypointIndex];
            const displayNumber = displayNumbers[waypointIndex];
            let timestamp: number | undefined;

            if (pilotIndex === 0 && this.plan!.departurePilotEtd > 0) {
                timestamp = this.plan!.departurePilotEtd;
            } else if (pilotIndex === pilotPointIndices.length - 1 && this.plan!.arrivalPilotEta > 0) {
                timestamp = this.plan!.arrivalPilotEta;
            } else if (waypoint.properties.eta) {
                const waypointEta = new Date(waypoint.properties.eta).getTime();
                if (!isNaN(waypointEta)) {
                    timestamp = waypointEta;
                }
            }

            if (timestamp !== undefined && displayNumber !== null) {
                entries.push({ displayNumber, timestamp });
            }

            return entries;
        }, []);
    }

    private _validateStoppageTimings(): void {
        const etdErrors = this._routeEditorValidationErrors.get("etd");
        if (!etdErrors) {
            return;
        }

        etdErrors.clear();

        const displayNumbers = this._getDisplayNumbers();
        const pilotTimingEntries = this._getPilotTimingEntries();

        this._waypoints.forEach((wp, idx) => {
            if (wp.properties.is_stoppage_point !== true || wp.properties.stoppage_end === true) {
                return;
            }

            const stoppageEta = wp.properties.eta ? new Date(wp.properties.eta).getTime() : NaN;
            const stoppageEtd = wp.properties.etd ? new Date(wp.properties.etd).getTime() : NaN;
            const displayNumber = displayNumbers[idx];

            if (displayNumber === null || isNaN(stoppageEta) || isNaN(stoppageEtd)) {
                return;
            }

            if (stoppageEtd < stoppageEta) {
                etdErrors.set(displayNumber, "ETD must be greater than ETA");
                return;
            }

            const conflictingPilot = pilotTimingEntries.find(({ timestamp }) => stoppageEta < timestamp && timestamp < stoppageEtd);
            if (conflictingPilot) {
                etdErrors.set(
                    displayNumber,
                    `Cannot re-calculate route due to stoppage WP ${displayNumber} overlapping pilot point WP ${conflictingPilot.displayNumber} ETA. Pilot point ETA cannot be between stoppage ETA and ETD.`
                );
            }
        });
    }

    private _updateMinSpeed(evt: Event, id: number) {
        const waypoint = this._getDisplayNumbers()[id]!;
        const legWaypointIndex = this._getLegWaypointIndex(id);
        const maxSpeed = this._vesselCharacteristics?.maxSpeed
        const vesselMaxSpeed = maxSpeed ? mpsToKnots(maxSpeed) : 60 // If vessel have no max speed set a high number to always pass validation

        this._deleteValidationError("min-speed", waypoint)

        const target = <HTMLInputElement>evt.target;
        if (!isNaN(target.valueAsNumber) && target.valueAsNumber < 0) {
            target.value = this._waypoints[legWaypointIndex].properties.speed_minimum ? (this._waypoints[legWaypointIndex].properties.speed_minimum! * MpsToKts).toFixed(1) : '';
            return;
        }

        const value = target.valueAsNumber
        let isValid = true
        let errorMessage = ""

        if (value > vesselMaxSpeed) {
            isValid = false;
            errorMessage = `Cannot re-calculate route due to WP ${waypoint} minimum speed (${value} knots) is set higher than vessel allowed max speed (${vesselMaxSpeed.toFixed(1)} knots)`;
        }

        const currentMaxSpeed = this._waypoints[legWaypointIndex].properties.speed_maximum
        if (currentMaxSpeed && value > mpsToKnots(currentMaxSpeed)) {
            isValid = false;
            errorMessage = `Cannot re-calculate route due to WP ${waypoint} minimum speed (${value} knots) is set higher than current provided max speed (${mpsToKnots(currentMaxSpeed)} knots)`;
        }

        if (!isValid) this._setFieldValidationError("min-speed", waypoint, errorMessage)

        const newMinSpeed = !isNaN(target.valueAsNumber) ? target.valueAsNumber / MpsToKts : undefined;
        const minSpeedWp = this._waypoints[legWaypointIndex];
        minSpeedWp.properties.speed_minimum = newMinSpeed;
        // Persist as userInput so the optimiser's response-merge does not overwrite it on recalc.
        minSpeedWp.properties.userInput = {
            ...minSpeedWp.properties.userInput,
            speed_minimum: newMinSpeed
        };
        RouteEditModel!.setNewRoute(this._waypoints);
        target.value.length > 0 ? target.value = Number(target.value).toFixed(1) : null;
    }

    private _updateMaxSpeed(evt: Event, id: number) {
        const waypoint = this._getDisplayNumbers()[id]!;
        const legWaypointIndex = this._getLegWaypointIndex(id);
        const maxSpeed = this._vesselCharacteristics?.maxSpeed
        const vesselMaxSpeed = maxSpeed ? mpsToKnots(maxSpeed) : 60 // If vessel have no max speed set a high number to always pass validation

        this._deleteValidationError("max-speed", waypoint)

        const target = <HTMLInputElement>evt.target;
        const value = target.valueAsNumber;
        let isValid = true;
        let errorMessage = ""

        if (!isNaN(target.valueAsNumber) && target.valueAsNumber === 0) {
            isValid = false;
            errorMessage = `Cannot re-calculate route due to WP ${waypoint} maximum speed is set to 0. Maximum speed must be greater than 0`;
        }
        if (!isNaN(target.valueAsNumber) && target.valueAsNumber < 0) {
            target.value = this._waypoints[legWaypointIndex].properties.speed_maximum ? (this._waypoints[legWaypointIndex].properties.speed_maximum! * MpsToKts).toFixed(1) : '';
            return;
        }

        if (value > vesselMaxSpeed) {
            isValid = false;
            errorMessage = `Cannot re-calculate route due to WP ${waypoint} maximum speed (${value} knots) is set higher than vessel allowed max speed (${vesselMaxSpeed.toFixed(1)} knots)`;
        }

        const currentMinSpeed = this._waypoints[legWaypointIndex].properties.speed_minimum
        if (currentMinSpeed && value < mpsToKnots(currentMinSpeed)) {
            isValid = false;
            errorMessage = `Cannot re-calculate route due to WP ${waypoint} minimum speed (${mpsToKnots(currentMinSpeed)} knots) is set higher than current provided max speed (${value} knots)`;
        }

        if (!isValid) this._setFieldValidationError("max-speed", waypoint, errorMessage)

        const newMaxSpeed = !isNaN(target.valueAsNumber) ? target.valueAsNumber / MpsToKts : undefined;
        const maxSpeedWp = this._waypoints[legWaypointIndex];
        maxSpeedWp.properties.speed_maximum = newMaxSpeed;
        // Persist as userInput so the optimiser's response-merge does not overwrite it on recalc.
        maxSpeedWp.properties.userInput = {
            ...maxSpeedWp.properties.userInput,
            speed_maximum: newMaxSpeed
        };
        RouteEditModel!.setNewRoute(this._waypoints);
        target.value.length > 0 ? target.value = Number(target.value).toFixed(1) : null;
    }

    private _updateWaterDepth(evt: Event, id: number) {
        const target = <HTMLInputElement>evt.target;
        const min_depth_value =
            this._waypoints[id].properties.userInput?.min_depth?.toFixed(1) ??
            this._waypoints[id].properties.min_depth?.toFixed(1) ??
            "";

        if (!isNaN(target.valueAsNumber) && target.valueAsNumber < 0) {
            target.value = min_depth_value;
            return;
        }

        const newMinDepth = !isNaN(target.valueAsNumber) ? target.valueAsNumber : undefined;
        const minDepthWp = this._waypoints[id];
        minDepthWp.properties.min_depth = newMinDepth;
        // Persist as userInput so the optimiser's response-merge does not overwrite it on recalc.
        minDepthWp.properties.userInput = {
            ...minDepthWp.properties.userInput,
            min_depth: newMinDepth
        }

        RouteEditModel!.setNewRoute(this._waypoints);
        target.value.length > 0 ? target.value = Number(target.value).toFixed(1) : null;
    }

    private _updateInputWithDate(eta: string | undefined) {
        if (!eta) return;
        return eta.substring(0, 16);
    }

    private _updateETA(eta: string, id: number) {
        if (this._isCurrentStoppagePoint(id)) {
            this.requestUpdate();
            return;
        }

        if (eta.length === 0) {
            this._deleteETA(id)
            return;
        }

        const localDateString = eta;
        // Guard against the full datetime not being input yet
        if (!Date.parse(localDateString)) {
            return NaN;
        }

        this._waypoints[id].properties.eta = localDateString.substring(0, 16) + ':00Z';
        this._syncStoppagePair(id);
        this._deleteValidationError("eta", this._getDisplayNumbers()[id]!);
        this._validateEtasChronological();
        this._validateStoppageTimings();
        RouteEditModel!.setNewRoute(this._waypoints);
    }

    private _updateETD(etd: string, id: number)
    {
        if (etd.length === 0)
        {
            this._deleteETD(id)
            return;
        }

        const localDateString = etd.substring(0, 16) + ':00Z';
        // Guard against the full datetime not being input yet
        if (!Date.parse(localDateString))
        {
            return NaN;
        }

        this._waypoints[id].properties.etd = localDateString;
        this._syncStoppagePair(id);
        this._validateStoppageTimings();
        RouteEditModel!.setNewRoute(this._waypoints);
    }

    private _deleteETA(id: number) {
        if (this._isCurrentStoppagePoint(id)) {
            this.requestUpdate();
            return;
        }

        this._waypoints[id].properties.eta = undefined;
        this._syncStoppagePair(id);
        this._deleteValidationError("eta", this._getDisplayNumbers()[id]!);
        this._validateEtasChronological();
        this._validateStoppageTimings();
        RouteEditModel!.setNewRoute(this._waypoints);
    }

    private _deleteETD(id: number) {
        this._waypoints[id].properties.etd = '';
        this._syncStoppagePair(id);
        this._validateStoppageTimings();
        RouteEditModel!.setNewRoute(this._waypoints);
    }

    private _focusOut(evt: FocusEvent, lonlat: string, id: number) {
        if ((<HTMLInputElement>evt.target).classList.contains("invalid")) {
            return;
        }

        let newValue = '';

        if (lonlat === "lon") newValue = CoordinatesUtils.decimalToDegreesAndMinutes(this._waypoints[id].geometry.coordinates).lon;
        if (lonlat === "lat") newValue = CoordinatesUtils.decimalToDegreesAndMinutes(this._waypoints[id].geometry.coordinates).lat;

        (<HTMLInputElement>evt.target).value = newValue;
        this.requestUpdate();
    }

    private _inputClick(evt: MouseEvent) {
        (<HTMLInputElement>evt.target).value = '';
    }

    private _populate() {
        if (this._waypoints.length === 0) {
            return html``;
        }

        const displayNumbers = this._getDisplayNumbers();
        return html`${this._waypoints.map((wp, index) => html`${this._renderRow(wp, index, displayNumbers[index])}`)}`;
    }

    private _revert() {
        RouteEditModel!.revert();
    }

    private _updateRoute() {
        RouteEditModel!.setNewRoute(this._waypoints);
    }


    /**
     * ==================================================================================
     * SCENARIO: UPON SAVE AND EXIT (Route Editor)
     * ==================================================================================
     * 
     * This method executes when user clicks "Save & Exit" in the route editor.
     * 
     * WHAT HAPPENS:
     * • Validates route (errors, pilot points, stoppage waypoints)
     * • Cleans up ETD properties from non-stoppage waypoints
     * • Gets or creates route object
     * • **CRITICAL: Checks alignment with Star Routes** (calls _setRouteAlignment)
     *   - Generates route geometry
     *   - Calls backend API: POST /bpr/adopted
     *   - Backend compares route geometry with star route(s)
     *   - Stores result: routeObject.bprAlignment = { isAligned: boolean, bprId: string }
     * • Saves and calculates route
     * • Closes editor
     * 
     * WHAT DETERMINES isAligned = true:
     * • Route geometry matches a Star Route within acceptable deviation limits
     * • Backend API /bpr/adopted returns { adopted: true }
     * • This means vessel's waypoints follow the star route path closely enough
     * 
     * WHY THIS MATTERS:
     * • The bprAlignment.isAligned value is stored with the route
     * • Later used in "Approve & Send" flow to determine:
     *   - Whether to show "exceed limits" popup
     *   - What notification reason to send to shore
     * 
     * FLOW CONTINUES TO: approveBtnClicked() when user clicks "Approve"
     */
    private async _saveAndClose(): Promise<void> {
        // A route whose shape no longer matches its calculation has to go through "Recalculate"
        // first - including one whose zone waypoints were just edited away, which is no longer in
        // the review to be stopped by the acknowledgement gate below.
        if (this._awaitingRecalculation) {
            return;
        }

        // The precautionary zone crossings have to be acknowledged before the route can be saved.
        if (this._inWarningsReview && !this._reviewAcknowledged) {
            return;
        }

        this._validateEtasChronological();
        this._validateStoppageTimings();

        if (!this._checkRouteValidity()) {
            VoyageDialogPopupManager.createDialog({
                headerText: "Validation errors",
                bodyContent: html`<voy-route-editor-validation-errors .errors=${this._routeEditorValidationErrors}></voy-route-editor-validation-errors>`,
                acceptButtonText: "Got it"
            })

            return
        }

        if (this._findAllPilotPointIndices().length < 2) {
            const dialog = VoyageDialogPopupManager.createDialog({
                headerText: "Missing Pilot Points",
                bodyText: `Route contains fewer than two Pilot Points.\nIf this is not intentional, return to Route Editor to add more Pilot Points`,
                acceptButtonText: "Proceed",
                rejectButtonText: "Return to Route Editor"
            });

            const response = await dialog.getResponse();
            if (response === "reject") {
                return
            }
        }

        if (this._isPassedAllWaypoints()) {
            const dialog = VoyageDialogPopupManager.createDialog({
                headerText: "All Waypoints Passed",
                bodyText: `All waypoints in the route are marked as passed.\nIf this is not intentional, return to Route Editor to update waypoint statuses.`,
                noAcceptButton: true,
                rejectButtonText: "Return to Route Editor"
            });

            const response = await dialog.getResponse();
            if (response === "reject") {
                return
            }
        }
        const stoppageWaypoints = this._waypoints.filter(wp => 
            wp.properties.is_stoppage_point === true && (wp.properties.etd === '' || wp.properties.eta === undefined)
        );
        if (stoppageWaypoints.length > 0 ) {
            VoyageDialogPopupManager.createDialog({
                headerText: "Incomplete Stoppage Waypoints",
                bodyText: "Stoppage waypoints do not have an ETD or ETA filled in. Please fill in before saving.",
                acceptButtonText: "Got it"
            });
            return; 
        }

        // Clean up etd property from non-stoppage waypoints
        this._waypoints.forEach(wp => {
            if (wp.properties.is_stoppage_point !== true) {
                delete wp.properties.etd;
            }
        });

        this._saving = true;

        const selectedRouteObject = this._getOrCreateRouteObject();

        // Ship the state of the review with the route: the routes list reads it back off route_info
        // to unlock "Select route" and "Passage Plan" and to show "Reviewed" instead of "Needs
        // Review", and saveRoute decides from it whether this save even needs a recalculation.
        // Written either way, so an acknowledgement copied off the route being edited cannot outlive
        // an edit that moved the crossings.
        // A route with zone crossings has always been calculated, so route_info is present.
        if (this._inWarningsReview && selectedRouteObject.route_info) {
            selectedRouteObject.route_info.routeWarningsReviewed = this._reviewAcknowledged;
        }

        const targetStarRouteId = this._getTargetStarRouteId(selectedRouteObject);

        // CRITICAL: Check and set route alignment with star routes
        // This populates routeObject.bprAlignment with { isAligned, bprId }
        // which is used later in approveBtnClicked() to determine notification behavior
        await this._setRouteAlignment(selectedRouteObject, targetStarRouteId);

        try {
            await RouteEditModel!.saveAndCalcRoute(selectedRouteObject);
            this._saving = false;
            this.setStarRouteLayerVisibility('', '');
            this.dispatchEvent(new Event(`close-editor`));
            RouteEditModel!.clear();
        } catch (error) {
            this._saving = false;
            console.error('Error saving and calculating route:', error);
        }
    }

    private _validateEtasChronological() {
        let previousEta = this.plan!.etd;
        const displayNumbers = this._getDisplayNumbers();
        const firstVisibleIndex = displayNumbers.findIndex(waypointNumber => waypointNumber !== null);

        this._waypoints.forEach((wp, idx) => {
            const waypointNumber = displayNumbers[idx];
            if (waypointNumber === null || idx === firstVisibleIndex || !wp.properties.eta) {
                // If the ETA is empty and there was a previous error remove it.
                if (waypointNumber !== null && this._routeEditorValidationErrors.get("eta")?.has(waypointNumber)) {
                    this._deleteValidationError("eta", waypointNumber);
                }

                return;
            }
            if (wp?.properties?.is_stoppage_point === true && wp?.properties?.stoppage_end !== undefined && wp.properties.stoppage_end === true) {
                return;
            }

            const eta = new Date(wp.properties.eta).getTime();
            const errorMessage = `Cannot re-calculate route due to ETAs not being in chronological order, WP ${waypointNumber} causes conflict.`;
            if (previousEta >= eta || eta > this.plan!.eta) {
                this._setFieldValidationError("eta", waypointNumber, errorMessage);
            }else {
                this._deleteValidationError("eta", waypointNumber);
            }

            previousEta = eta;
        });
    }

    private _midPoint(coord1: [number, number], coord2: [number, number]): [number, number] {
        const lat = (coord1[1] + coord2[1]) / 2;
        const lon1 = coord1[0];
        let lon2 = coord2[0];
        if (Math.abs(lon2 - lon1) > 180) lon2 += Math.sign(lon1) * 360;
        let lon = (lon1 + lon2) / 2;
        while (lon < -180) lon += 360;
        while (lon > 180) lon -= 360;
        return [lon, lat];
    }

    private _add(id: number) {
        const nextVisibleWaypointIndex = this._getNextVisibleWaypointIndex(id);
        if (nextVisibleWaypointIndex < 0) {
            return;
        }

        const legWaypointIndex = this._getLegWaypointIndex(id);
        const newCoord = this._midPoint(this._waypoints[id].geometry.coordinates, this._waypoints[nextVisibleWaypointIndex].geometry.coordinates);
        const newWaypoint = this._newWaypoint(newCoord);
        this._waypoints.splice(nextVisibleWaypointIndex, 0, newWaypoint);

        // Set leg_type based on distance
        if (nextVisibleWaypointIndex < this._waypoints.length - 1) {
            newWaypoint.properties.leg_type = getLegTypeFromDist(newCoord, this._waypoints[nextVisibleWaypointIndex + 1].geometry.coordinates);
        }

        // Leg type for the waypoint before the new waypoint needs to be set as well
        this._waypoints[legWaypointIndex].properties.leg_type = getLegTypeFromDist(this._waypoints[id].geometry.coordinates, newCoord);

        this._clearAllValidationErrors();
        this.requestUpdate();
        this._updateRoute();
        this._validateEtasChronological();
        this._validateStoppageTimings();
    }

    private _newWaypoint(coords: [number, number]): VoyageRoutePointFeature {
        return {
            type: "Feature",
            geometry: {
                coordinates: coords,
                type: "Point",
            },
            properties: { type: "Hard", userInserted: true }
        };
    }

    private _updateLon(evt: InputEvent, id: number) {
        const target = <VoyLatLongInput>evt.target;
        if (!target.invalid && target.value !== null) {
            this._waypoints[id].geometry.coordinates[0] = target.value;
            this._syncStoppagePair(id);
            this._updateRoute();
        }
    }

    private _updateLat(evt: InputEvent, id: number) {
        const target = <VoyLatLongInput>evt.target;
        if (!target.invalid && target.value !== null) {
            this._waypoints[id].geometry.coordinates[1] = target.value;
            this._syncStoppagePair(id);
            this._updateRoute();
        }
    }

    private async _delete(id: number) {
        const dialog = VoyageDialogPopupManager.createDialog({
            headerText: "Deleting the waypoint",
            bodyText: "Do you want to delete the waypoint?",
            acceptButtonText: "Accept",
            rejectButtonText: "Cancel"
        });

        const response = await dialog.getResponse();
        if (response === "accept") {
            this._removeStoppagePair(id);
            this._waypoints.splice(id, 1);

            // Need to update leg_type on previous waypoint
            if (id > 0 && id < this._waypoints.length) {
                this._waypoints[id - 1].properties.leg_type = getLegTypeFromDist(this._waypoints[id - 1].geometry.coordinates, this._waypoints[id].geometry.coordinates);
            }

            this._clearAllValidationErrors();
            this.requestUpdate();
            this._updateRoute();
            this._validateEtasChronological();
            this._validateStoppageTimings();
        }
    }

    private _updatePilotPointInfo() {
        this._pilotOutIndex = -1;
        this._pilotInIndex = -1;
        let pilotPointIndeces = this._findAllPilotPointIndices();

        // Pilot points
        if (pilotPointIndeces.length >= 2) {
            // Set indexes
            this._pilotOutIndex = pilotPointIndeces[0];
            this._pilotInIndex = pilotPointIndeces[pilotPointIndeces.length - 1];
        }
        else if (pilotPointIndeces.length === 1) {
            // Assume single pilot point is always at the destination
            this._pilotInIndex = pilotPointIndeces[0];
        }
    }

    private _getDistances(waypoints: VoyageRoutePointFeature[]) {

        const getLength = (wps: VoyageRoutePointFeature[]) => wps.map(wp => wp.properties.distance).reduce((acc, wp) => acc + wp, 0);
        const pilotToPilotWp = waypoints.slice(this._pilotOutIndex === -1 ? 0 : this._pilotOutIndex, this._pilotInIndex === -1 ? waypoints.length : this._pilotInIndex);

        let pilotToPilotLength = "";

        if (pilotToPilotWp.length > 0) {
            pilotToPilotLength = getLength(pilotToPilotWp).toFixed(0);
        }

        const allLength = getLength(waypoints).toFixed(0);

        return { allLength, pilotToPilotLength };
    }

    private _onRouteChanged(evt: CustomEvent<{ waypoints: VoyageRoutePointFeature[] }>) {
        const parsedWaypoints = JSON.parse(JSON.stringify(evt.detail.waypoints));
        const filteredWaypoints = parsedWaypoints.filter(filterConditionNavigational);
        this._waypoints = filteredWaypoints;
        this._updatePilotPointInfo();
        this._distances = this._getDistances(this._waypoints.slice(0, -1));
        this._refreshZoneCrossings();
    }

    private _onSelectionChanged() {
        this.requestUpdate();
        const elemToScroll = this.shadowRoot!.getElementById(`${RouteEditModel!.selectedWaypointIndex}`);
        if (elemToScroll) {
            const sideBar = this.shadowRoot!.getElementById('waypoints-list');
            ScrollTo(sideBar!, (elemToScroll.offsetTop - sideBar!.offsetTop) - 20, 500);
        }
    }

    private _findAllPilotPointIndices() {
        const pilotPointIndeces = [];

        for (let i = 0; i < this._waypoints.length; i++) {
            if (this._waypoints[i].properties.pilotPoint) {
                pilotPointIndeces.push(i);
            }
        }
        return pilotPointIndeces;
    }

    private _isPassedAllWaypoints() {
        const isPassedAllWaypoints = [];

        for (let i = 0; i < this._waypoints.length; i++) {
            if (this._waypoints[i].properties.isPassed) {
                isPassedAllWaypoints.push(i);
            }
        }
        return (this._waypoints.length <= isPassedAllWaypoints.length);
    }

    private _getOrCreateRouteObject() {
        const { uiRoutes } = VoyageRoutingModel!;
        const uiRouteIndex = RouteEditModel!.uiRouteIndex;
        const uiRoute = uiRoutes[uiRouteIndex];

        const routeId = uiRoute?.routeId;
        const weatherRoutingInfo = uiRoute?.route?.weatherRoutingInfo;

        const existingRoute = VoyageRoutingModel!.routes.find(route => route.routeId === routeId);
        const routeObject = existingRoute ? deepCopy(existingRoute) : RouteEditModel!.generateRoute();

        routeObject.route_geometry = RouteEditModel!.generateRouteGeometry();
        routeObject.recalcNeeded = true;
        routeObject.weatherRoutingInfo = weatherRoutingInfo;

        // Take the route_info the routes list is showing, which is the freshest calculated one - but
        // as a copy, and never as an absence: this route object is what the review acknowledgement
        // gets stamped on, and a route saved without route_info counts as a failed calculation.
        const latestRouteInfo = uiRoute?.route?.route_info ?? routeObject.route_info;
        if (latestRouteInfo) {
            routeObject.route_info = deepCopy(latestRouteInfo);
        }

        return routeObject;
    }

    private _getTargetStarRouteId(routeObject: any): string | undefined {
        if (this.selectedStarRoute) {
            return this.selectedStarRoute;
        }

        const bprId = routeObject?.bprAlignment?.bprId;
        if (!bprId) {
            return undefined;
        }

        const routes = this.plan?.routes ?? VoyageRoutingModel!.routes;
        return routes.find(route => route.bestPracticeRouteId === bprId)?.routeId ?? bprId;
    }

    /**
     * ==================================================================================
     * SAVE & EXIT HELPER: Set Route Alignment
     * ==================================================================================
     * 
     * Called during Save & Exit to check route alignment with Star Routes.
     * 
     * WHAT THIS DOES:
     * • If targetStarRouteId provided: Checks against that specific star route
     * • If no targetStarRouteId: Checks against ALL available star routes
     * 
     * BACKEND API CALL:
     * • POST /bpr/adopted with { route, bprID }
     * • Returns { adopted: boolean }
     * • "adopted: true" means route geometry is aligned within acceptable limits
     * 
     * RESULT STORED:
     * • routeObject.bprAlignment = { isAligned: boolean, bprId: string }
     * • This data is used later in Pre-Departure approval flow
     */
    private async _setRouteAlignment(routeObject: any, targetStarRouteId: string | undefined): Promise<void> {
        const topStarRoute = getMostFuelEfficientStarRoute(this.plan?.routes ?? VoyageRoutingModel!.routes);
        const starRouteId = targetStarRouteId ?? topStarRoute?.routeId;
        if (starRouteId) {
            await this._checkAlignmentWithSpecificStarRoute(routeObject, starRouteId);
        }
    }

    private async _checkAlignmentWithSpecificStarRoute(routeObject: any, starRouteId: string): Promise<void> {
        const uiRouteIndex = RouteEditModel!.uiRouteIndex;

        if (uiRouteIndex === -1) {
            return;
        }

        try {
            routeObject.route_geometry = RouteEditModel!.generateRouteGeometry();

            const routes = this.plan?.routes ?? VoyageRoutingModel!.routes;
            const starRoute = routes.find(route => route.routeId === starRouteId);
            if (!starRoute?.bestPracticeRouteId) {
                routeObject.bprAlignment = { isAligned: false, bprId: '' };
                return;
            }

            const adoption = await VesselPlanningModel!.getRouteAdoption(
                routeObject,
                starRoute.bestPracticeRouteId,
                undefined,
                starRoute
            );
            // Persist the deviation-annotated geometry the backend returned, so the exceeded
            // waypoints survive the save and render as amber. Gated so the feature can ship dark.
            if (isStarRouteDeviationEnabled() && adoption.route?.route_geometry) {
                routeObject.route_geometry = adoption.route.route_geometry;
            }
            routeObject.bprAlignment = { isAligned: adoption.adopted, bprId: starRoute.bestPracticeRouteId };
        } catch (error) {
            console.error('Error checking route alignment:', error);
        }
    }

    private async _checkAlignmentWithAllStarRoutes(routeObject: any): Promise<void> {
        const starRoutes = this.plan!.routes.filter(route => route.routeCreatedBy === 'system');

        for (const starRoute of starRoutes) {
            try {
                const isAligned = await this._checkRouteAlignment(routeObject, starRoute.routeId);
                routeObject.bprAlignment = { isAligned, bprId: starRoute.bestPracticeRouteId ?? '' };

                if (isAligned) {
                    break; // Stop checking once we find an aligned route
                }
            } catch (error) {
                console.error('Error checking route alignment:', error);
            }
        }
    }
}
